import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Button,
    Box,
    Typography,
    TextField,
    CircularProgress,
    Alert,
    useTheme,
    alpha
} from '@mui/material';
import {
    LocalShipping as LocalShippingIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import DemandService from '../services/DemandService';

/**
 * PackageModal - модалка ввода числа мест для упаковки.
 *
 * Props:
 *   open: boolean              - состояние открытия
 *   onClose: () => void        - закрыть модалку (также вызывается при Cancel)
 *   demandId: string           - UUID demand (отгрузки)
 *   demandName: string         - имя/номер отгрузки для отображения
 *   onSuccess: (data) => void  - callback успеха, data = { pdfBase64, packagesCount, cdekOrderUuid }
 *
 * Поведение:
 *   - Валидация: целое число >= 1 (иначе кнопка "Распечатать" disabled + helperText)
 *   - Submit -> DemandService.packageDemand(demandId, N)
 *   - Обработка 400/404/409/503 с понятными сообщениями
 *   - При 503 (СДЭК недоступен) автоматически вызывается /retry один раз;
 *     при повторном сбое показывается generic alert
 */
export default function PackageModal({ open, onClose, demandId, demandName, onSuccess }) {
    const theme = useTheme();

    const [packagesCount, setPackagesCount] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [errorSeverity, setErrorSeverity] = useState('error'); // 'error' | 'warning'

    // Сброс состояния при открытии/закрытии
    useEffect(() => {
        if (open) {
            setPackagesCount('');
            setLoading(false);
            setError(null);
            setErrorSeverity('error');
        }
    }, [open]);

    const reset = () => {
        setPackagesCount('');
        setLoading(false);
        setError(null);
        setErrorSeverity('error');
    };

    const handleClose = () => {
        if (loading) return; // не закрывать во время запроса
        reset();
        onClose();
    };

    const handleSubmit = async (e) => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        const n = parseInt(packagesCount, 10);
        if (!Number.isInteger(n) || n < 1) {
            setError('Введите целое число, большее или равное 1');
            setErrorSeverity('warning');
            return;
        }
        setLoading(true);
        setError(null);
        setErrorSeverity('error');
        try {
            const response = await DemandService.packageDemand(demandId, n);
            const data = (response && response.data) ? response.data : response;
            reset();
            onSuccess(data);
        } catch (err) {
            const status = err && err.response && err.response.status;
            if (status === 503) {
                // Auto-retry: СДЭК моргнул. Ждём 2 сек и молча повторяем через
                // /retry (packagesCount берётся из Redis pending - тот же N).
                // loading остаётся true, поле ввода заблокировано. При повторном
                // сбое показываем generic alert.
                try {
                    await new Promise(r => setTimeout(r, 2000));
                    const retryResp = await DemandService.retryPackage(demandId);
                    const retryData = (retryResp && retryResp.data) ? retryResp.data : retryResp;
                    reset();
                    onSuccess(retryData);
                    return;
                } catch (retryErr) {
                    setError('PDF временно недоступен. Закройте окно и попробуйте позже, либо обратитесь к менеджеру.');
                    setErrorSeverity('error');
                    return;
                }
            }
            mapPackageError(err, { setError, setErrorSeverity });
        } finally {
            setLoading(false);
        }
    };

    // Валидность ввода: целое число >= 1
    const parsed = parseInt(packagesCount, 10);
    const isValid = Number.isInteger(parsed) && parsed >= 1;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            disableEscapeKeyDown={loading}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingIcon color="primary" />
                <Typography variant="h6" component="span">
                    Упаковка заказа
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    {demandName && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Отгрузка: <strong>{demandName}</strong>
                        </Typography>
                    )}

                    {!demandName && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Укажите количество мест в отправлении.
                        </Typography>
                    )}

                    <form onSubmit={handleSubmit} id="package-modal-form">
                        <TextField
                            label="Сколько мест?"
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                            value={packagesCount}
                            onChange={(e) => setPackagesCount(e.target.value)}
                            disabled={loading}
                            autoFocus
                            fullWidth
                            error={Boolean(packagesCount) && !isValid}
                            helperText={
                                !packagesCount
                                    ? 'Введите целое число, большее или равное 1'
                                    : !isValid
                                        ? 'Значение должно быть целым числом >= 1'
                                        : ' '
                            }
                        />
                    </form>
                </Box>

                {error && (
                    <Alert
                        severity={errorSeverity}
                        sx={{ mt: 1 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'primary.main' }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Обновляем грузоместа и загружаем ярлыки...</Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    color="inherit"
                    sx={{
                        borderColor: alpha(theme.palette.text.primary, 0.3),
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.text.primary, 0.04)
                        }
                    }}
                    variant="outlined"
                >
                    Отмена
                </Button>

                <Button
                    type="submit"
                    form="package-modal-form"
                    onClick={handleSubmit}
                    disabled={!isValid || loading}
                    color="primary"
                    variant="contained"
                    startIcon={<PrintIcon />}
                >
                    {loading ? 'Выполняется...' : 'Распечатать'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Маппинг ошибок цепочки упаковки в человекочитаемые сообщения.
 * Вынесено в функцию - вызывается из handleSubmit для не-503 ошибок.
 */
function mapPackageError(err, setters) {
    const { setError, setErrorSeverity } = setters;
    const status = err && err.response && err.response.status;
    const data = err && err.response && err.response.data;
    const errCode =
        (data && data.errors && data.errors[0] && data.errors[0].code) ||
        (data && data.error);

    if (status === 400) {
        if (errCode === 'NO_TRACKING_NUMBER') {
            setError('На отгрузке не задан атрибут "Трек-номер", обратитесь к менеджеру');
        } else if (errCode === 'INVALID_PACKAGES_COUNT') {
            setError('Введите целое число, большее или равное 1');
        } else if (errCode === 'ITEMS_LESS_THAN_PACKAGES') {
            const max = data?.errors?.[0]?.totalUnits;
            setError(max
                ? `Уменьшите количество мест. В заказе всего ${max} шт. товара - больше коробок сделать нельзя.`
                : 'Слишком много мест для этого заказа. Уменьшите количество.'
            );
        } else if (errCode === 'BARCODES_LESS_THAN_PACKAGES') {
            const max = data?.errors?.[0]?.distinctBarcodes;
            setError(max
                ? `Уменьшите количество мест. В заказе ${max} разных товаров - больше коробок сделать нельзя.`
                : 'Слишком много мест для этого заказа. Уменьшите количество.'
            );
        } else {
            setError('Ошибка валидации: ' + ((data && data.message) || (err && err.message) || ''));
        }
        setErrorSeverity('warning');
    } else if (status === 404) {
        setError('Отгрузка не найдена');
        setErrorSeverity('error');
    } else if (status === 409) {
        if (errCode === 'CDEK_ORDER_NOT_EDITABLE') {
            setError('Отправление уже в пути, обратитесь к менеджеру');
        } else if (errCode === 'ALREADY_PRINTED') {
            setError('Наклейки уже распечатаны');
        } else {
            setError('Действие невозможно: ' + ((data && data.message) || ''));
        }
        setErrorSeverity('error');
    } else if (status === 503) {
        // На случай, если auto-retry отключён или 503 пришёл не из цепочки СДЭК.
        // На практике после auto-retry в handleSubmit сюда уже не доходим.
        setError('СДЭК временно недоступен. Попробуйте позже.');
        setErrorSeverity('warning');
    } else {
        setError('Ошибка: ' + ((err && err.message) || 'неизвестная'));
        setErrorSeverity('error');
    }
}
